/**
 * Paddle.js singleton + overlay checkout.
 *
 * Paddle has no hosted checkout page. A transaction's `checkout.url` is our own
 * default payment link with `?_ptxn=<id>` appended, and that page has to run
 * Paddle.js itself to open anything. Since Paddle.js is required either way, we
 * skip the round-trip and open the overlay directly from the SPA — no full-page
 * redirect out of and back into the app.
 *
 * The client-side token is NOT the API key. It's public by design and safe in
 * the bundle; sandbox tokens start with `test_`.
 */
import { initializePaddle, type Paddle } from '@paddle/paddle-js';

let paddlePromise: Promise<Paddle | undefined> | null = null;

/** Initialize Paddle.js once per page load and reuse the instance. */
export function getPaddle(): Promise<Paddle | undefined> {
  if (!paddlePromise) {
    const token = import.meta.env.VITE_PADDLE_CLIENT_TOKEN as string | undefined;
    if (!token) {
      paddlePromise = Promise.reject(
        new Error(
          'VITE_PADDLE_CLIENT_TOKEN is not set — self-serve checkout is unavailable.'
        )
      );
      // Don't leave an unhandled rejection if nobody awaits it this tick.
      paddlePromise.catch(() => undefined);
      return paddlePromise;
    }
    paddlePromise = initializePaddle({
      environment:
        (import.meta.env.VITE_PADDLE_ENVIRONMENT as 'sandbox' | 'production') ?? 'sandbox',
      token,
    });
  }
  return paddlePromise;
}

export interface OpenCheckoutOptions {
  /**
   * Show the overlay's "add discount" field. `false` for credit top-ups: a
   * top-up is priced to the cent server-side and no code is meant for one, and
   * the webhook refuses a discounted top-up outright — so offering the field
   * would only let a buyer pay and then be refused. Plan checkouts keep it.
   */
  allowDiscounts?: boolean;
}

/**
 * Open the overlay checkout for a transaction created server-side.
 *
 * `onCompleted` fires on Paddle's `checkout.completed` event. That event means
 * "the customer finished paying", NOT "the plan is granted" — the webhook is the
 * only thing that grants a plan, so callers should navigate to the success page
 * and poll `/billing/status` rather than assume the upgrade has landed.
 */
export async function openCheckout(
  transactionId: string,
  onCompleted: () => void,
  { allowDiscounts = true }: OpenCheckoutOptions = {}
): Promise<void> {
  const paddle = await getPaddle();
  if (!paddle) throw new Error('Paddle.js failed to initialize');

  paddle.Update({
    eventCallback: (event) => {
      if (event.name !== 'checkout.completed') return;
      // CLOSE THE OVERLAY OURSELVES. Paddle leaves its "transaction completed"
      // panel up until the user finds the ×, and our own success screen is
      // already rendered UNDERNEATH it — so the buyer's last impression of
      // paying us is a modal they have to dismiss to discover anything
      // happened. Closing is not a shortcut past a confirmation: Paddle emails
      // its own receipt, and the screen behind this is the one that tells them
      // what to do next.
      //
      // Wrapped because it is cosmetic. If a future Paddle.js drops or renames
      // `close`, the checkout still completed and `onCompleted` must still
      // run — failing the success path over a dismissal would be a far worse
      // bug than the one this fixes.
      try {
        paddle.Checkout.close();
      } catch {
        // Leave the panel up; the user can still dismiss it by hand.
      }
      onCompleted();
    },
  });
  paddle.Checkout.open({
    transactionId,
    settings: {
      // The transaction already names the buyer's Paddle customer — the server
      // bound it before minting. Letting the buyer switch email in the overlay
      // would move the purchase to a customer the webhook cannot tie to this
      // account, and it refuses such an event rather than guess (A1-1).
      allowLogout: false,
      showAddDiscounts: allowDiscounts,
    },
  });
}

/** Reset the memoized instance. Test-only. */
export function __resetPaddleForTests(): void {
  paddlePromise = null;
}
